// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	dismissInstanceAnnouncement,
	openInstanceAnnouncementTarget,
	resolveInstanceAnnouncement,
} from '@app/features/app/components/layout/app_layout/InstanceAnnouncementNotice';
import styles from '@app/features/app/components/layout/app_layout/nagbars/InstanceAnnouncementNagbar.module.css';
import {Nagbar} from '@app/features/app/components/layout/Nagbar';
import {NagbarButton} from '@app/features/app/components/layout/NagbarButton';
import {NagbarContent} from '@app/features/app/components/layout/NagbarContent';
import {NAGBAR_TONES, type NagbarTone, NagbarToneKind} from '@app/features/app/components/layout/NagbarTones';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {channelIconUrl} from '@app/features/channel/components/channel_icon/ChannelIconUrl';
import NagbarState from '@app/features/ui/state/Nagbar';
import * as LocaleUtils from '@app/features/user/utils/LocaleUtils';
import {isChannelIconKey} from '@fluxer/constants/src/ChannelIcons';
import type {InstanceAnnouncementTone} from '@fluxer/schema/src/domains/instance/InstanceAnnouncementSchemas';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';

const ANNOUNCEMENT_TONES: Readonly<Record<InstanceAnnouncementTone, NagbarTone>> = {
	purple: NAGBAR_TONES[NagbarToneKind.BRAND],
	blue: NAGBAR_TONES[NagbarToneKind.MAINTENANCE_SCHEDULED],
	green: NAGBAR_TONES[NagbarToneKind.VOICE],
	orange: NAGBAR_TONES[NagbarToneKind.ALERT],
	red: NAGBAR_TONES[NagbarToneKind.CRITICAL],
	dark: {backgroundColor: '#232428', textColor: '#ffffff'},
};

function AnnouncementIcon({iconKey}: {iconKey: string | null}) {
	if (!isChannelIconKey(iconKey)) return null;
	if (iconKey.startsWith('e:')) {
		return (
			<img
				src={channelIconUrl(iconKey)}
				alt=""
				aria-hidden={true}
				draggable={false}
				className={styles.icon}
				data-flx="app.app-layout.nagbars.instance-announcement-nagbar.emoji-icon"
			/>
		);
	}
	return (
		<span
			aria-hidden={true}
			className={clsx(styles.icon, styles.solarIcon)}
			style={{maskImage: `url(${channelIconUrl(iconKey)})`}}
			data-flx="app.app-layout.nagbars.instance-announcement-nagbar.solar-icon"
		/>
	);
}

export const InstanceAnnouncementNagbar = observer(({isMobile}: {isMobile: boolean}) => {
	void NagbarState.instanceAnnouncementDismissalVersion;
	const announcement = resolveInstanceAnnouncement(
		RuntimeConfig.announcement,
		Date.now(),
		LocaleUtils.getCurrentLocale(),
	);
	if (!announcement) return null;
	const tone = ANNOUNCEMENT_TONES[announcement.tone];
	const handleDismiss = () => {
		dismissInstanceAnnouncement(announcement.id);
		NagbarState.bumpInstanceAnnouncementDismissed();
	};
	return (
		<Nagbar
			isMobile={isMobile}
			backgroundColor={tone.backgroundColor}
			textColor={tone.textColor}
			dismissible
			onDismiss={handleDismiss}
			data-flx="app.app-layout.nagbars.instance-announcement-nagbar.nagbar"
		>
			<NagbarContent
				isMobile={isMobile}
				onDismiss={handleDismiss}
				icon={
					<AnnouncementIcon
						iconKey={announcement.icon}
						data-flx="app.app-layout.nagbars.instance-announcement-nagbar.announcement-icon"
					/>
				}
				message={announcement.text}
				actions={
					announcement.buttonText ? (
						<NagbarButton
							isMobile={isMobile}
							onClick={() => openInstanceAnnouncementTarget(announcement)}
							data-flx="app.app-layout.nagbars.instance-announcement-nagbar.nagbar-button.open"
						>
							{announcement.buttonText}
						</NagbarButton>
					) : undefined
				}
				data-flx="app.app-layout.nagbars.instance-announcement-nagbar.nagbar-content"
			/>
		</Nagbar>
	);
});
